import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Save, Loader2, Plus, Trash2, GripVertical, Type, Heading, Image, Minus, Square, Code, Video, MousePointerClick } from 'lucide-react';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_TYPES = [
    { value: 'splash', label: 'Splash Page' },
    { value: 'interstitial', label: 'Interstitial' },
    { value: 'thank_you', label: 'Thank You Page' },
    { value: 'redirect', label: 'Redirect Page' },
];

const COMPONENT_TYPES = [
    { type: 'heading', label: 'Heading', icon: Type },
    { type: 'subheading', label: 'Subheading', icon: Heading },
    { type: 'button', label: 'Button', icon: MousePointerClick },
    { type: 'image', label: 'Image', icon: Image },
    { type: 'divider', label: 'Divider', icon: Minus },
    { type: 'spacer', label: 'Spacer', icon: Square },
    { type: 'html_block', label: 'HTML Block', icon: Code },
    { type: 'video', label: 'Video', icon: Video },
];

const DEFAULT_SPLASH = {
    page_type: 'thank_you',
    trigger_submit: false,
    blocks: [
        { id: 'blk_1', type: 'heading', text: 'Thank You!', size: '28px', color: '#1C1917', alignment: 'center' },
        { id: 'blk_2', type: 'subheading', text: "We'll be in touch soon.", size: '16px', color: '#57534E', alignment: 'center' },
        { id: 'blk_3', type: 'button', text: 'Back to Home', url: '/', bg_color: '#1C1917', text_color: '#FFFFFF', alignment: 'center' },
    ],
};

function makeBlock(type) {
    const id = 'blk_' + Math.random().toString(36).substr(2, 7);
    switch (type) {
        case 'heading':
            return { id, type, text: 'New Heading', size: '24px', color: '#1C1917', alignment: 'left' };
        case 'subheading':
            return { id, type, text: 'New subheading text', size: '15px', color: '#57534E', alignment: 'left' };
        case 'button':
            return { id, type, text: 'Click Here', url: '/', bg_color: '#1C1917', text_color: '#FFFFFF', alignment: 'left' };
        case 'image':
            return { id, type, src: '', alt: '', width: '100%' };
        case 'divider':
            return { id, type, color: '#E8E5DF', thickness: '1px' };
        case 'spacer':
            return { id, type, height: '32px' };
        case 'html_block':
            return { id, type, html_content: '<p>Custom HTML here</p>' };
        case 'video':
            return { id, type, url: '', width: '100%' };
        default:
            return { id, type, text: '' };
    }
}

// ─── Block Renderer (preview) ────────────────────────────────────────────────

function BlockPreview({ block }) {
    const align = block.alignment || 'left';
    switch (block.type) {
        case 'heading':
            return (
                <div style={{ textAlign: align }}>
                    <div style={{ fontSize: block.size || '24px', color: block.color || '#1C1917', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
                        {block.text || 'Heading'}
                    </div>
                </div>
            );
        case 'subheading':
            return (
                <div style={{ textAlign: align }}>
                    <div style={{ fontSize: block.size || '15px', color: block.color || '#57534E', fontWeight: 400 }}>
                        {block.text || 'Subheading'}
                    </div>
                </div>
            );
        case 'button':
            return (
                <div style={{ textAlign: align }}>
                    <span style={{
                        display: 'inline-block', padding: '10px 20px', borderRadius: '8px',
                        backgroundColor: block.bg_color || '#1C1917', color: block.text_color || '#FFFFFF',
                        fontSize: '14px', fontWeight: 600, textDecoration: 'none',
                    }}>
                        {block.text || 'Button'}
                    </span>
                </div>
            );
        case 'image':
            return block.src ? (
                <img src={block.src} alt={block.alt || ''} style={{ width: block.width || '100%', borderRadius: '6px' }} />
            ) : (
                <div style={{ width: block.width || '100%', height: '120px', background: '#F1F0EB', border: '1px dashed #D6D3D1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A29D96', fontSize: '12px' }}>
                    <Image className="w-5 h-5" />
                </div>
            );
        case 'divider':
            return <hr style={{ border: 'none', borderTop: `${block.thickness || '1px'} solid ${block.color || '#E8E5DF'}`, margin: 0 }} />;
        case 'spacer':
            return <div style={{ height: block.height || '32px' }} />;
        case 'html_block':
            return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.html_content || '') }} />;
        case 'video':
            return block.url ? (
                <div style={{ width: block.width || '100%' }}>
                    <video src={block.url} controls style={{ width: '100%', borderRadius: '6px' }} />
                </div>
            ) : (
                <div style={{ width: block.width || '100%', height: '120px', background: '#F1F0EB', border: '1px dashed #D6D3D1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A29D96', fontSize: '12px' }}>
                    <Video className="w-5 h-5" />
                </div>
            );
        default:
            return null;
    }
}

// ─── Properties Field ───────────────────────────────────────────────────────

function PropField({ label, children }) {
    return (
        <div className="cpd-prop-row">
            <label className="cpd-prop-label">{label}</label>
            {children}
        </div>
    );
}

const CPD_INPUT = 'cpd-prop-input';

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CustomPageDesigner() {
    const { formId, stepId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();

    const [form, setForm] = useState(null);
    const [step, setStep] = useState(null);
    const [splash, setSplash] = useState(DEFAULT_SPLASH);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [hasManualSaved, setHasManualSaved] = useState(false);

    const autoSaveTimer = useRef(null);

    // ── Load form ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!formId || !stepId) { toast.error('Missing form or step ID'); navigate('/forms'); return; }
        axios.get(`${API}/forms/${formId}`, { headers: getAuthHeader() })
            .then(res => {
                const f = res.data;
                setForm(f);
                const foundStep = (f.steps || []).find(s => (s.step_id || s.id) === stepId);
                if (!foundStep) { toast.error('Step not found'); navigate(`/forms/${formId}/workflow`); return; }
                setStep(foundStep);
                if (foundStep.splash && foundStep.splash.blocks) {
                    setSplash(foundStep.splash);
                }
            })
            .catch(() => { toast.error('Form not found'); navigate('/forms'); })
            .finally(() => setLoading(false));
    }, [formId, stepId, getAuthHeader, navigate]);

    // ── Dirty tracking ─────────────────────────────────────────────────────
    useEffect(() => { setIsDirty(true); }, [splash]);

    // ── Debounced auto-save (only after first manual save) ──────────────────
    useEffect(() => {
        if (!isDirty || !hasManualSaved || !form) return;
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => { doSave(true); }, 2500);
        return () => clearTimeout(autoSaveTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [splash, isDirty, hasManualSaved]);

    // ── beforeunload guard ─────────────────────────────────────────────────
    useEffect(() => {
        const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // ── Save ───────────────────────────────────────────────────────────────
    const doSave = async (isAuto = false) => {
        if (!form || !step) return;
        if (!isAuto) setSaving(true);
        try {
            const updatedSteps = (form.steps || []).map(s =>
                (s.step_id || s.id) === stepId ? { ...s, splash } : s
            );
            const payload = { ...form, steps: updatedSteps, status: form.status || 'draft' };
            const res = await axios.put(`${API}/forms/${formId}`, payload, { headers: getAuthHeader() });
            setForm(res.data);
            setIsDirty(false);
            if (!isAuto) toast.success('Page saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            if (!isAuto) setSaving(false);
        }
    };

    const handleSave = () => { doSave(false); setHasManualSaved(true); };

    // ── Block operations ───────────────────────────────────────────────────
    const addBlock = (type) => {
        const newBlock = makeBlock(type);
        setSplash(prev => ({ ...prev, blocks: [...prev.blocks, newBlock] }));
        setSelectedBlockId(newBlock.id);
    };

    const updateBlock = (blockId, updates) => {
        setSplash(prev => ({
            ...prev,
            blocks: prev.blocks.map(b => (b.id === blockId ? { ...b, ...updates } : b)),
        }));
    };

    const deleteBlock = (blockId) => {
        setSplash(prev => ({ ...prev, blocks: prev.blocks.filter(b => b.id !== blockId) }));
        if (selectedBlockId === blockId) setSelectedBlockId(null);
    };

    const moveBlock = (index, dir) => {
        const t = index + dir;
        if (t < 0 || t >= splash.blocks.length) return;
        const next = [...splash.blocks];
        [next[index], next[t]] = [next[t], next[index]];
        setSplash(prev => ({ ...prev, blocks: next }));
    };

    const duplicateBlock = (blockId) => {
        const block = splash.blocks.find(b => b.id === blockId);
        if (!block) return;
        const idx = splash.blocks.findIndex(b => b.id === blockId);
        const copy = { ...block, id: 'blk_' + Math.random().toString(36).substr(2, 7) };
        const next = [...splash.blocks];
        next.splice(idx + 1, 0, copy);
        setSplash(prev => ({ ...prev, blocks: next }));
        setSelectedBlockId(copy.id);
    };

    // ── Page type change ───────────────────────────────────────────────────
    const setPageType = (pageType) => {
        setSplash(prev => ({ ...prev, page_type: pageType }));
    };

    // ── Drag and drop reorder ──────────────────────────────────────────────
    const handleDragStart = (e, index) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDrop = (e, toIndex) => {
        e.preventDefault();
        e.stopPropagation();
        if (dragIndex != null && dragIndex !== toIndex) {
            const next = [...splash.blocks];
            const [moved] = next.splice(dragIndex, 1);
            next.splice(toIndex, 0, moved);
            setSplash(prev => ({ ...prev, blocks: next }));
        }
        setDragIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    // ── Derived ────────────────────────────────────────────────────────────
    const selectedBlock = splash.blocks.find(b => b.id === selectedBlockId) || null;

    // ── Loading state ──────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="flex flex-1 items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <>
            <style>{`
                /* ── Sub-header ── */
                .cpd-sub-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 24px; border-bottom: 1px solid #E8E5DF; background: #FFFFFF; flex-shrink: 0; gap: 12px; }
                .cpd-sub-header-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
                .cpd-sub-header h2 { font-size: 16px; font-weight: 700; color: #1C1917; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .cpd-sub-header-actions { display: flex; gap: 8px; flex-shrink: 0; }
                .cpd-back-btn { width: 32px; height: 32px; border-radius: 6px; color: #57534E; display: flex; align-items: center; justify-content: center; transition: background .15s; cursor: pointer; border: none; background: transparent; }
                .cpd-back-btn:hover { background: #F1F0EB; }
                .cpd-btn-outline { padding: 6px 14px; border-radius: 6px; border: 1px solid #E8E5DF; font-size: 13px; font-weight: 500; color: #57534E; background: #FFFFFF; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background .15s; }
                .cpd-btn-outline:hover { background: #F1F0EB; }
                .cpd-btn-outline:disabled { opacity: .5; cursor: not-allowed; }
                .cpd-btn-primary { padding: 6px 14px; border-radius: 6px; background: #1C1917; color: #F7F6F2; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; border: none; transition: background .15s; }
                .cpd-btn-primary:hover { background: #292524; }
                .cpd-btn-primary:disabled { opacity: .5; cursor: not-allowed; }

                /* ── Layout ── */
                .cpd-layout { display: flex; flex: 1; overflow: hidden; }

                /* ── Left: Component Palette ── */
                .cpd-palette { width: 220px; background: #FFFFFF; border-right: 1px solid #E8E5DF; padding: 16px; overflow-y: auto; flex-shrink: 0; }
                .cpd-palette-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #6E6961; margin-bottom: 12px; }
                .cpd-palette-section { margin-bottom: 24px; }
                .cpd-page-pills { display: flex; flex-direction: column; gap: 6px; }
                .cpd-page-pill { padding: 8px 12px; border-radius: 8px; border: 1px solid #E8E5DF; font-size: 12px; font-weight: 500; color: #57534E; background: #FFFFFF; cursor: pointer; transition: all .15s; text-align: left; }
                .cpd-page-pill:hover { background: #F1F0EB; border-color: #D6D3D1; }
                .cpd-page-pill.active { background: rgba(99,102,241,0.08); border-color: #6366F1; color: #4F46E5; }
                .cpd-trigger-toggle { display: flex; align-items: flex-start; gap: 8px; padding: 10px; border-radius: 8px; border: 1px solid #E8E5DF; background: #FFFFFF; cursor: pointer; transition: all .15s; }
                .cpd-trigger-toggle:hover { background: #F1F0EB; border-color: #D6D3D1; }
                .cpd-trigger-toggle input[type="checkbox"] { margin-top: 2px; width: 16px; height: 16px; accent-color: #6366F1; cursor: pointer; flex-shrink: 0; }
                .cpd-trigger-label { font-size: 12px; font-weight: 600; color: #1C1917; line-height: 1.4; }
                .cpd-trigger-hint { font-size: 11px; color: #A29D96; margin-top: 6px; line-height: 1.4; }

                .cpd-comp-item { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 8px; border: 1px solid #E8E5DF; margin-bottom: 6px; cursor: pointer; transition: all .15s; background: #FFFFFF; }
                .cpd-comp-item:hover { background: #F1F0EB; border-color: #D6D3D1; }
                .cpd-comp-icon { width: 28px; height: 28px; border-radius: 6px; background: #F7F6F2; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #57534E; }
                .cpd-comp-label { font-size: 12px; font-weight: 500; color: #1C1917; }

                /* ── Center: Visual Preview ── */
                .cpd-preview-area { flex: 1; overflow-y: auto; background: #F7F6F2; padding: 24px; display: flex; flex-direction: column; align-items: center; }
                .cpd-browser-bar { width: 100%; max-width: 640px; background: #FFFFFF; border: 1px solid #E8E5DF; border-bottom: none; border-radius: 10px 10px 0 0; padding: 10px 14px; display: flex; align-items: center; gap: 8px; }
                .cpd-browser-dots { display: flex; gap: 6px; }
                .cpd-browser-dot { width: 10px; height: 10px; border-radius: 50%; }
                .cpd-browser-url { flex: 1; background: #F7F6F2; border-radius: 6px; padding: 5px 12px; font-size: 11px; color: #A29D96; font-family: monospace; }
                .cpd-preview-card { width: 100%; max-width: 640px; background: #FFFFFF; border: 1px solid #E8E5DF; border-radius: 0 0 10px 10px; padding: 32px 28px; min-height: 300px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
                .cpd-preview-empty { text-align: center; padding: 48px 0; color: #A29D96; font-size: 13px; }
                .cpd-block-wrapper { position: relative; padding: 8px 10px; margin: 2px 0; border: 1.5px solid transparent; border-radius: 6px; cursor: pointer; transition: border-color .12s, background .12s; }
                .cpd-block-wrapper:hover { border-color: #D6D3D1; background: rgba(247,246,242,0.5); }
                .cpd-block-wrapper.selected { border-color: #6366F1; background: rgba(99,102,241,0.04); }
                .cpd-block-wrapper.dragging { opacity: 0.4; }
                .cpd-block-wrapper.drag-over { border-color: #6366F1; border-style: dashed; }
                .cpd-block-grip { position: absolute; left: -2px; top: 50%; transform: translateY(-50%); opacity: 0; cursor: grab; color: #A29D96; transition: opacity .12s; }
                .cpd-block-wrapper:hover .cpd-block-grip, .cpd-block-wrapper.selected .cpd-block-grip { opacity: 1; }
                .cpd-block-actions { position: absolute; top: -10px; right: -8px; display: none; gap: 4px; z-index: 10; }
                .cpd-block-wrapper.selected .cpd-block-actions { display: flex; }
                .cpd-block-action-btn { width: 22px; height: 22px; border-radius: 50%; background: #FFFFFF; border: 1px solid #E8E5DF; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #57534E; transition: background .12s; }
                .cpd-block-action-btn:hover { background: #F1F0EB; }
                .cpd-block-action-btn.danger:hover { background: #FBE7E8; color: #C2363F; border-color: #C2363F; }

                /* ── Right: Properties Panel ── */
                .cpd-props-panel { width: 280px; background: #FFFFFF; border-left: 1px solid #E8E5DF; padding: 16px; overflow-y: auto; flex-shrink: 0; }
                .cpd-props-title { font-size: 14px; font-weight: 700; color: #1C1917; margin-bottom: 4px; }
                .cpd-props-subtitle { font-size: 11px; color: #A29D96; margin-bottom: 16px; text-transform: uppercase; letter-spacing: 0.04em; }
                .cpd-props-empty { font-size: 13px; color: #A29D96; padding: 24px 0; text-align: center; }
                .cpd-prop-row { margin-bottom: 14px; }
                .cpd-prop-label { display: block; font-size: 11px; font-weight: 600; color: #6E6961; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.04em; }
                .cpd-prop-input { width: 100%; padding: 8px 10px; border: 1px solid #E8E5DF; border-radius: 6px; font-size: 13px; color: #1C1917; background: #FFFFFF; font-family: inherit; box-sizing: border-box; }
                .cpd-prop-input:focus { outline: none; border-color: #1C1917; }
                textarea.cpd-prop-input { resize: vertical; min-height: 80px; font-family: monospace; font-size: 12px; }
                .cpd-prop-color-row { display: flex; align-items: center; gap: 8px; }
                .cpd-prop-color { width: 36px; height: 36px; border: 1px solid #E8E5DF; border-radius: 6px; cursor: pointer; padding: 2px; background: #FFFFFF; flex-shrink: 0; }
                .cpd-align-group { display: flex; gap: 4px; }
                .cpd-align-btn { flex: 1; padding: 7px; border: 1px solid #E8E5DF; border-radius: 6px; background: #FFFFFF; cursor: pointer; font-size: 11px; font-weight: 500; color: #57534E; transition: all .12s; text-transform: capitalize; }
                .cpd-align-btn:hover { background: #F1F0EB; }
                .cpd-align-btn.active { background: #1C1917; color: #FFFFFF; border-color: #1C1917; }
                .cpd-props-divider { border-top: 1px solid #F1F0EB; margin: 16px 0; }
                .cpd-props-actions { display: flex; gap: 8px; flex-wrap: wrap; }
                .cpd-btn-sm { padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 4px; border: none; transition: background .12s; }
                .cpd-btn-sm-danger { background: #FBE7E8; color: #C2363F; }
                .cpd-btn-sm-danger:hover { background: #F3D3D5; }
                .cpd-btn-sm-secondary { background: #F1F0EB; color: #57534E; }
                .cpd-btn-sm-secondary:hover { background: #E8E5DF; }
                .cpd-save-status { font-size: 12px; color: #A29D96; }
            `}</style>

            <div className="flex-1 min-h-0 flex flex-col bg-stone-50">
                {/* Sub-header */}
                <div className="cpd-sub-header">
                    <div className="cpd-sub-header-left">
                        <button
                            onClick={() => navigate(`/forms/${formId}/workflow`)}
                            className="cpd-back-btn"
                            title="Back to workflow"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <h2>Custom Page Designer — {step?.title || 'Untitled Step'}</h2>
                    </div>
                    <div className="cpd-sub-header-actions">
                        <span className="cpd-save-status">
                            {isDirty && hasManualSaved && 'Saving...'}
                            {!isDirty && hasManualSaved && '✓ Saved'}
                            {isDirty && !hasManualSaved && 'Unsaved changes'}
                        </span>
                        <button className="cpd-btn-outline" onClick={handleSave} disabled={saving}>
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            Save
                        </button>
                    </div>
                </div>

                {/* Designer layout */}
                <div className="cpd-layout">
                    {/* Left: Component Palette */}
                    <div className="cpd-palette">
                        <div className="cpd-palette-section">
                            <div className="cpd-palette-title">Page Type</div>
                            <div className="cpd-page-pills">
                                {PAGE_TYPES.map(pt => (
                                    <button
                                        key={pt.value}
                                        className={`cpd-page-pill ${splash.page_type === pt.value ? 'active' : ''}`}
                                        onClick={() => setPageType(pt.value)}
                                    >
                                        {pt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Trigger Submit Toggle — when enabled, reaching this
                            custom page immediately submits the form data.
                            The page content serves as the thank-you screen.
                            No Continue button is shown. */}
                        <div className="cpd-palette-section">
                            <div className="cpd-palette-title">Submission</div>
                            <label className="cpd-trigger-toggle">
                                <input
                                    type="checkbox"
                                    checked={!!splash.trigger_submit}
                                    onChange={(e) => setSplash(prev => ({ ...prev, trigger_submit: e.target.checked }))}
                                />
                                <span className="cpd-trigger-label">Trigger form submission on reach</span>
                            </label>
                            <p className="cpd-trigger-hint">
                                When enabled, the form submits automatically when the user reaches this page.
                                The page content serves as the thank-you screen — no Continue button is shown.
                            </p>
                        </div>

                        <div className="cpd-palette-section">
                            <div className="cpd-palette-title">Components</div>
                            {COMPONENT_TYPES.map(ct => {
                                const Icon = ct.icon;
                                return (
                                    <div
                                        key={ct.type}
                                        className="cpd-comp-item"
                                        onClick={() => addBlock(ct.type)}
                                        title={`Add ${ct.label}`}
                                    >
                                        <div className="cpd-comp-icon">
                                            <Icon className="w-3.5 h-3.5" />
                                        </div>
                                        <span className="cpd-comp-label">{ct.label}</span>
                                        <Plus className="w-3 h-3 text-stone-400 ml-auto" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Center: Visual Preview */}
                    <div className="cpd-preview-area" onClick={() => setSelectedBlockId(null)}>
                        {/* Browser bar mockup */}
                        <div className="cpd-browser-bar">
                            <div className="cpd-browser-dots">
                                <span className="cpd-browser-dot" style={{ background: '#FF5F57' }} />
                                <span className="cpd-browser-dot" style={{ background: '#FEBC2E' }} />
                                <span className="cpd-browser-dot" style={{ background: '#28C840' }} />
                            </div>
                            <div className="cpd-browser-url">
                                forms.nurturecrm.ai/page/{stepId}
                            </div>
                        </div>

                        {/* Preview card */}
                        <div className="cpd-preview-card" onClick={e => e.stopPropagation()}>
                            {splash.blocks.length === 0 ? (
                                <div className="cpd-preview-empty">
                                    No components yet. Click a component in the left palette to add it.
                                </div>
                            ) : (
                                splash.blocks.map((block, index) => {
                                    const isSelected = selectedBlockId === block.id;
                                    const isDragging = dragIndex === index;
                                    const isDragOver = dragOverIndex === index && dragIndex !== index;
                                    return (
                                        <div
                                            key={block.id}
                                            className={`cpd-block-wrapper ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragEnd={handleDragEnd}
                                            onClick={(e) => { e.stopPropagation(); setSelectedBlockId(block.id); }}
                                        >
                                            {/* Grip handle */}
                                            <span className="cpd-block-grip">
                                                <GripVertical className="w-3.5 h-3.5" />
                                            </span>

                                            {/* Quick actions */}
                                            <div className="cpd-block-actions">
                                                <button
                                                    className="cpd-block-action-btn"
                                                    onClick={(e) => { e.stopPropagation(); moveBlock(index, -1); }}
                                                    title="Move up"
                                                    disabled={index === 0}
                                                    style={index === 0 ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                                                >
                                                    <ChevronLeft className="w-3 h-3" style={{ transform: 'rotate(90deg)' }} />
                                                </button>
                                                <button
                                                    className="cpd-block-action-btn"
                                                    onClick={(e) => { e.stopPropagation(); moveBlock(index, 1); }}
                                                    title="Move down"
                                                    disabled={index === splash.blocks.length - 1}
                                                    style={index === splash.blocks.length - 1 ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                                                >
                                                    <ChevronLeft className="w-3 h-3" style={{ transform: 'rotate(-90deg)' }} />
                                                </button>
                                                <button
                                                    className="cpd-block-action-btn danger"
                                                    onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}
                                                    title="Delete block"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>

                                            {/* Block content */}
                                            <BlockPreview block={block} />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Right: Properties Panel */}
                    <div className="cpd-props-panel">
                        {!selectedBlock ? (
                            <div className="cpd-props-empty">
                                Click a component in the preview to edit its properties.
                            </div>
                        ) : (
                            <div>
                                <div className="cpd-props-title">Properties</div>
                                <div className="cpd-props-subtitle">
                                    {COMPONENT_TYPES.find(c => c.type === selectedBlock.type)?.label || selectedBlock.type}
                                </div>

                                {/* Text-based blocks */}
                                {(selectedBlock.type === 'heading' || selectedBlock.type === 'subheading') && (
                                    <>
                                        <PropField label="Text">
                                            <textarea
                                                className={CPD_INPUT}
                                                value={selectedBlock.text || ''}
                                                onChange={e => updateBlock(selectedBlock.id, { text: e.target.value })}
                                                rows={2}
                                            />
                                        </PropField>
                                        <PropField label="Size (px)">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.size || ''}
                                                onChange={e => updateBlock(selectedBlock.id, { size: e.target.value })}
                                                placeholder="24px"
                                            />
                                        </PropField>
                                        <PropField label="Color">
                                            <div className="cpd-prop-color-row">
                                                <input
                                                    type="color"
                                                    className="cpd-prop-color"
                                                    value={selectedBlock.color || '#1C1917'}
                                                    onChange={e => updateBlock(selectedBlock.id, { color: e.target.value })}
                                                />
                                                <input
                                                    className={CPD_INPUT}
                                                    type="text"
                                                    value={selectedBlock.color || ''}
                                                    onChange={e => updateBlock(selectedBlock.id, { color: e.target.value })}
                                                />
                                            </div>
                                        </PropField>
                                        <PropField label="Alignment">
                                            <div className="cpd-align-group">
                                                {['left', 'center', 'right'].map(a => (
                                                    <button
                                                        key={a}
                                                        className={`cpd-align-btn ${selectedBlock.alignment === a ? 'active' : ''}`}
                                                        onClick={() => updateBlock(selectedBlock.id, { alignment: a })}
                                                    >
                                                        {a}
                                                    </button>
                                                ))}
                                            </div>
                                        </PropField>
                                    </>
                                )}

                                {/* Button */}
                                {selectedBlock.type === 'button' && (
                                    <>
                                        <PropField label="Button Text">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.text || ''}
                                                onChange={e => updateBlock(selectedBlock.id, { text: e.target.value })}
                                            />
                                        </PropField>
                                        <PropField label="URL">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.url || ''}
                                                onChange={e => updateBlock(selectedBlock.id, { url: e.target.value })}
                                                placeholder="/thank-you"
                                            />
                                        </PropField>
                                        <PropField label="Background Color">
                                            <div className="cpd-prop-color-row">
                                                <input
                                                    type="color"
                                                    className="cpd-prop-color"
                                                    value={selectedBlock.bg_color || '#1C1917'}
                                                    onChange={e => updateBlock(selectedBlock.id, { bg_color: e.target.value })}
                                                />
                                                <input
                                                    className={CPD_INPUT}
                                                    type="text"
                                                    value={selectedBlock.bg_color || ''}
                                                    onChange={e => updateBlock(selectedBlock.id, { bg_color: e.target.value })}
                                                />
                                            </div>
                                        </PropField>
                                        <PropField label="Text Color">
                                            <div className="cpd-prop-color-row">
                                                <input
                                                    type="color"
                                                    className="cpd-prop-color"
                                                    value={selectedBlock.text_color || '#FFFFFF'}
                                                    onChange={e => updateBlock(selectedBlock.id, { text_color: e.target.value })}
                                                />
                                                <input
                                                    className={CPD_INPUT}
                                                    type="text"
                                                    value={selectedBlock.text_color || ''}
                                                    onChange={e => updateBlock(selectedBlock.id, { text_color: e.target.value })}
                                                />
                                            </div>
                                        </PropField>
                                        <PropField label="Alignment">
                                            <div className="cpd-align-group">
                                                {['left', 'center', 'right'].map(a => (
                                                    <button
                                                        key={a}
                                                        className={`cpd-align-btn ${selectedBlock.alignment === a ? 'active' : ''}`}
                                                        onClick={() => updateBlock(selectedBlock.id, { alignment: a })}
                                                    >
                                                        {a}
                                                    </button>
                                                ))}
                                            </div>
                                        </PropField>
                                    </>
                                )}

                                {/* Image */}
                                {selectedBlock.type === 'image' && (
                                    <>
                                        <PropField label="Image URL">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.src || ''}
                                                onChange={e => updateBlock(selectedBlock.id, { src: e.target.value })}
                                                placeholder="https://..."
                                            />
                                        </PropField>
                                        <PropField label="Alt Text">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.alt || ''}
                                                onChange={e => updateBlock(selectedBlock.id, { alt: e.target.value })}
                                            />
                                        </PropField>
                                        <PropField label="Width">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.width || '100%'}
                                                onChange={e => updateBlock(selectedBlock.id, { width: e.target.value })}
                                                placeholder="100% or 320px"
                                            />
                                        </PropField>
                                    </>
                                )}

                                {/* Divider */}
                                {selectedBlock.type === 'divider' && (
                                    <>
                                        <PropField label="Color">
                                            <div className="cpd-prop-color-row">
                                                <input
                                                    type="color"
                                                    className="cpd-prop-color"
                                                    value={selectedBlock.color || '#E8E5DF'}
                                                    onChange={e => updateBlock(selectedBlock.id, { color: e.target.value })}
                                                />
                                                <input
                                                    className={CPD_INPUT}
                                                    type="text"
                                                    value={selectedBlock.color || ''}
                                                    onChange={e => updateBlock(selectedBlock.id, { color: e.target.value })}
                                                />
                                            </div>
                                        </PropField>
                                        <PropField label="Thickness">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.thickness || '1px'}
                                                onChange={e => updateBlock(selectedBlock.id, { thickness: e.target.value })}
                                                placeholder="1px, 2px..."
                                            />
                                        </PropField>
                                    </>
                                )}

                                {/* Spacer */}
                                {selectedBlock.type === 'spacer' && (
                                    <PropField label="Height (px)">
                                        <input
                                            className={CPD_INPUT}
                                            type="text"
                                            value={selectedBlock.height || '32px'}
                                            onChange={e => updateBlock(selectedBlock.id, { height: e.target.value })}
                                            placeholder="32px"
                                        />
                                    </PropField>
                                )}

                                {/* HTML Block */}
                                {selectedBlock.type === 'html_block' && (
                                    <PropField label="HTML Content">
                                        <textarea
                                            className={CPD_INPUT}
                                            value={selectedBlock.html_content || ''}
                                            onChange={e => updateBlock(selectedBlock.id, { html_content: e.target.value })}
                                            rows={8}
                                            placeholder="<p>Custom HTML</p>"
                                        />
                                        <p style={{ fontSize: '10px', color: '#A29D96', marginTop: 4 }}>
                                            HTML is sanitized with DOMPurify before rendering.
                                        </p>
                                    </PropField>
                                )}

                                {/* Video */}
                                {selectedBlock.type === 'video' && (
                                    <>
                                        <PropField label="Video URL">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.url || ''}
                                                onChange={e => updateBlock(selectedBlock.id, { url: e.target.value })}
                                                placeholder="https://..."
                                            />
                                        </PropField>
                                        <PropField label="Width">
                                            <input
                                                className={CPD_INPUT}
                                                type="text"
                                                value={selectedBlock.width || '100%'}
                                                onChange={e => updateBlock(selectedBlock.id, { width: e.target.value })}
                                                placeholder="100% or 640px"
                                            />
                                        </PropField>
                                    </>
                                )}

                                {/* Common actions */}
                                <div className="cpd-props-divider" />
                                <div className="cpd-props-actions">
                                    <button
                                        className="cpd-btn-sm cpd-btn-sm-secondary"
                                        onClick={() => duplicateBlock(selectedBlock.id)}
                                    >
                                        <Plus className="w-3 h-3" />
                                        Duplicate
                                    </button>
                                    <button
                                        className="cpd-btn-sm cpd-btn-sm-danger"
                                        onClick={() => deleteBlock(selectedBlock.id)}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
